import { ValueFormatterParams } from "ag-grid-community";
import React, { useCallback, useRef, useState } from "react";
import PlusButtonIcon from "../../../../assets/images/PlusButtonIcon.svg";
import Grid from "../../../../components/Grid";
import { ColumnDef } from "../../../../components/Grid/model/Grid";
import ModalDelete from "../../../../components/ModalDelete";
import Loading from "../../../../core/common/Loading";
import { formatDateTimeBR } from "../../../../helpers/format";

import { useModal } from "../../../../hooks/ModalContext";
import api from "../../../../services/api";
import CreateOperacaoPatioCancela from "./Create";
import Info from "./Info";
import { IOperacoesPatioCancela } from "./types/types";
import Can from "../../../../components/Can";

const ListOperacoesPatioCancelas: React.FC = () => {
  const [activeView, setActiveView] = useState<"sulog" | "spe">("sulog");
  const [columns] = useState<ColumnDef[]>([
    {
      field: "numero_cancela",
      headerName: "Número Cancela",
      filter: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return params.value;
        }
        return "---";
      },
    },
    {
      field: "tipo_cancela",
      headerName: "Tipo Cancela",
      filter: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value === 1 || params.value === "1" || params.value === "ENTRADA") {
          return "ENTRADA";
        }
        if (params.value === 2 || params.value === "2" || params.value === "SAIDA") {
          return "SAIDA";
        }
        return params.value || "---";
      },
    },
    {
      field: "ip_camera_dianteira",
      headerName: "IP Câmera Dianteira",
      filter: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return params.value;
        }
        return "---";
      },
    },
    {
      field: "ip_controladora",
      headerName: "IP Controladora",
      filter: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return params.value;
        }
        return "---";
      },
    },
    {
      field: "atuar_na_cancela",
      headerName: "Atuar na Cancela",
      filter: true,
      valueFormatter: (params: ValueFormatterParams) => {
        return params.value ? "Sim" : "Não";
      },
    },
    {
      field: "usuario",
      headerName: "Usuário",
      filter: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return params.value;
        }
        return "---";
      },
    },
    {
      field: "data_historico",
      headerName: "Data Modificação",
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return formatDateTimeBR(params.value);
        }
        return "---";
      },
    },
  ]);

  const [selectedRow, setSelectedRow] = useState<IOperacoesPatioCancela>();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isView, setIsView] = useState<boolean>(false);
  const [isRemove, setIsRemove] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const gridRef = useRef<any>(null);
  const { isModalOpen, closeModal, openModal } = useModal();

  const onDelete = useCallback(async (entityId?: number) => {
    try {
      setLoading(true);

      const body = {
        id_operacao_patio_cancela: entityId,
      };

      const endpoint = activeView === "spe" ? "/deletar/cancelasSpe" : "/deletar/cancelas";
      await api.post(endpoint, body);

      setLoading(false);
      setIsRemove(false);
      gridRef.current?.refresh();
    } catch {
      setLoading(false);
    }
  }, [activeView]);

  return (
    <>
      <Loading loading={loading} />

      {isModalOpen && (
        <CreateOperacaoPatioCancela
          isView={isView}
          isEdit={isEdit}
          isSpe={activeView === "spe"}
          selectedRow={selectedRow}
          onClear={() => {
            closeModal();
            setIsEdit(false);
          }}
          onConfirm={() => {
            closeModal();
            gridRef.current?.refresh();
          }}
        />
      )}
      {isRemove && (
        <ModalDelete
          onCancel={() => setIsRemove(!isRemove)}
          onConfirm={() => onDelete(selectedRow?.id_operacao_patio_cancela)}
          row={String(selectedRow?.numero_cancela)}
        />
      )}

      {isView && (
        <Info
          data={selectedRow}
          title={`Conhecer - Operações Pátio Cancelas ${activeView === "spe" ? "SPE" : "Sulog"}`}
          onClose={() => setIsView(!isView)}
        />
      )}

      <div className="flex flex-col w-full h-screen bg-[#F5F5F5] p-5">
        <div className="flex items-center justify-between w-full mb-3">
          <div>
            <h1 className="text-2xl text-[#000000] font-bold">
              Cancelas {activeView === "spe" ? "(SPE)" : "(Sulog)"}
            </h1>
          </div>

          <div className="mr-14 flex items-center gap-4">
            {/* Pill Switch Buttons: Sulog | SPE */}
            <div className="flex items-center shrink-0 gap-2">
              <button
                className={
                  `flex items-center justify-center h-10 px-5 text-sm font-semibold rounded-full transition-colors ` +
                  (activeView === "sulog"
                    ? "bg-[#062D4E] text-white border-none shadow-sm"
                    : "bg-white border border-[#CCCCCC] text-[#062D4E] hover:bg-gray-100")
                }
                onClick={() => setActiveView("sulog")}
              >
                Sulog
              </button>

              <button
                className={
                  `flex items-center justify-center h-10 px-5 text-sm font-semibold rounded-full transition-colors ` +
                  (activeView === "spe"
                    ? "bg-[#062D4E] text-white border-none shadow-sm"
                    : "bg-white border border-[#062D4E] text-[#062D4E] hover:bg-blue-50")
                }
                onClick={() => setActiveView("spe")}
              >
                SPE
              </button>
            </div>

            <Can acao="SALVAR">
            <button
              className="flex items-center justify-center h-12 w-36 bg-[#062D4E] text-[#FFFFFF] text-sm font-light border-none rounded-full"
              onClick={() => openModal()}
              id="btnCreate"
            >
              Adicionar <img src={PlusButtonIcon} alt="" className="ml-2" />
            </button>
            </Can>
          </div>
        </div>

        <div className="flex w-full">
          <Grid
            ref={gridRef}
            key={activeView}
            columns={columns}
            filters={[]}
            pagination
            path={activeView === "spe" ? "/listar/cancelasSpe" : "/listar/cancelas"}
            onUpdate={(data: IOperacoesPatioCancela) => {
              setSelectedRow(data);
              setIsEdit(!isEdit);
              openModal();
            }}
            onDelete={(data: IOperacoesPatioCancela) => {
              setIsRemove(!isRemove);
              setSelectedRow(data);
            }}
            onView={(data: IOperacoesPatioCancela) => {
              setSelectedRow(data);
              setIsView(!isView);
            }}
            isShowStatus={false}
            status={[]}
          />
        </div>
      </div>
    </>
  );
};

export default ListOperacoesPatioCancelas;
